import eventsApi from 'api/requests/events';
import Button from 'components/_UI/Button';
import LinkPage from 'components/_UI/LinkPage';
import Loader from 'components/_UI/Loader';
import DynamicProfileBackground from 'components/commons/DynamicProfileBackground';
import { useBackButtonCollapseDash } from 'context/dash/BackButtonCollapseDash';
import { useShowAllEvents } from 'context/dash/ShowAllEvents';
import { useEvent } from 'context/events/Event';
import date from 'helpers/date';
import types from 'helpers/system/types';
import TicketsForSale from 'modules/dashboard/events/detail/tickets/TicketsForSale';
import EventAccessCard from 'modules/dashboard/events/detail/analytics/EventAccessCard';
import MaskedStats from 'modules/dashboard/events/collaboration/MaskedStats';
import useEventAccess from 'hooks/useEventAccess';
import { EVENT_PERMISSIONS } from 'helpers/eventCollaboration';
import CronogramModal from 'modules/events/detail/CronogramModal';
import React, { useState, useEffect } from 'react'
import { Copy, MapPin, Calendar, ChevronDown, ChevronUp, ExternalLink, Lock } from 'lucide-react';
import { copyToClipboard } from 'helpers/clipboard';
import styled, { keyframes } from 'styled-components'
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors';
import eventTypes from 'theme/eventTypes';
import ProductsForSale from '../products/ProductsForSale';
import breakpoints from 'theme/breakpoints';
import InfoTooltip from 'components/_UI/InfoTooltip';
import ToastContainer from 'components/_UI/ToastsContainer';
import useToast from 'hooks/useToast';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const truncateHTML = (html, limit) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  const textContent = div.textContent || div.innerText || '';
  if (textContent.length <= limit) return html;
  div.innerHTML = textContent.slice(0, limit) + '...';
  return div.innerHTML;
};

export default function EventDetail({
  item,
  withBackButton = false,
  hasTickets,
  setHasTickets,
  setHasProducts,
  hasProducts,
  isDraft = false
}) {
  const [fullCronogramModal, setFullCronogramModal] = useState(false)
  const { showAllEvents, setShowAllEvents } = useShowAllEvents()
  const [showFullText, setShowFullText] = useState(false);
  const [visibleBackButton, setVisibleBackButton] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [offlineStats, setOfflineStats] = useState(null);
  const [offlineProductStats, setOfflineProductStats] = useState(null);
  const { backButtonCollapseDash, setBackButtonCollapseDash } = useBackButtonCollapseDash()
  const { toasts, toast } = useToast();
  const { setEvent } = useEvent();
  const { can } = useEventAccess();

  const eventUrl = `${import.meta.env.VITE_CLIENT_URL}/evento/${data?.slug}`;

  /* O link público não revela nada oculto: quem abre ele não vê o ingresso e
     não tem como comprar. Cada item oculto precisa do seu link — o mesmo
     endereço com o código aplicado — e é esse que o produtor manda. */
  const hiddenItems = (data?.products || []).filter(el => el?.hidden_code);

  function hiddenItemUrl(item) {
    return `${eventUrl}?hidden_code=${encodeURIComponent(item.hidden_code)}`;
  }

  useEffect(() => {
    getEvent(item)
  }, [])

  const getEvent = async (slug) => {
    setLoading(true)
    try {
      const { data } = await eventsApi.getBySlug(slug)
      setData(data?.data)
      setEvent(data?.data);
      setHasTickets(data?.data?.products.some(el => el.type === 'ticket'))
      setHasProducts(data?.data?.products.some(el => el.type === 'product'))
    } catch (error) {
    } finally {
      setLoading(false)
    }

    try {
      const { data: ticketsRes } = await eventsApi.getTickets(slug);
      const sold = ticketsRes?.tickets_sold_offline;
      const revenue = ticketsRes?.tickets_revenue_offline;
      if (sold > 0 || revenue > 0) {
        setOfflineStats({ sold, revenue });
      }

      const soldProducts = ticketsRes?.products_sold_offline;
      const revenueProducts = ticketsRes?.products_revenue_offline;
      if (soldProducts > 0 || revenueProducts > 0) {
        setOfflineProductStats({ sold: soldProducts, revenue: revenueProducts });
      }
    } catch {}
  }

  const handleShowMore = () => setShowFullText(!showFullText);

  function copyLink(value, message) {
    copyToClipboard(value, () => toast.success(message, { position: 'top-center' }));
  }

  function coppyEventUrl() {
    copyLink(eventUrl, 'Link copiado com sucesso!');
  }

  return (
    <ContainerDetail>
      <ToastContainer toasts={toasts} />

      {!loading ? (
        <>
          {/* ── HERO CARD ──
              Mesma proporção e recorte da capa pública real do evento
              (modules/events/page/cover/PageCover.js → StyledBackground:
              object-fit: cover, 200px de altura no mobile, 280px no desktop)
              — antes essa faixa vinha em 220/300px, maior que o real, o que
              fazia até imagem de baixa qualidade parecer mais nítida/grande
              do que realmente vai ficar pro público. */}
          <HeroCard>
            <HeroImageWrap>
              <DynamicProfileBackground
                img={data?.images[0]}
                event
              />
            </HeroImageWrap>

            <HeroBody>
              {/* Badge row */}
              <BadgeRow>
                <StatusBadge active={data?.is_past}>
                  {data?.is_past ? 'Encerrado' : 'Ativo'}
                </StatusBadge>

                <TypeBadge type={data?.classification?.name}>
                  {types.getBySlug(data?.classification?.name)?.label}
                </TypeBadge>

                {data?.adulthood && <AdultBadge>+18</AdultBadge>}

                {data?.tags?.map(el => (
                  <TagBadge key={el.name}>{el.name}</TagBadge>
                ))}
              </BadgeRow>

              {/* Event name */}
              <EventName>{data?.name}</EventName>

              {/* Public link */}
              {!isDraft && (
                <LinkRow>
                  <ExternalLink size={14} color={panelAccent(colors.secondaryPurple)} />
                  <LinkPage isWrapper to={eventUrl}>
                    <LinkText>{eventUrl}</LinkText>
                  </LinkPage>
                  <InfoTooltip
                    bgColor={colors.night}
                    text='Copiar link do evento'
                    type='none'
                  >
                    <CopyButton onClick={coppyEventUrl}>
                      <Copy size={12} color={colors.neutralLight} />
                    </CopyButton>
                  </InfoTooltip>
                </LinkRow>
              )}

              {!isDraft && hiddenItems.length > 0 && (
                <HiddenLinks>
                  <HiddenLinksLabel>
                    <Lock size={12} strokeWidth={2.5} aria-hidden='true' />
                    {hiddenItems.length === 1
                      ? 'Link com o item oculto liberado'
                      : 'Links com os itens ocultos liberados'}
                  </HiddenLinksLabel>

                  {hiddenItems.map(el => (
                    <HiddenLinkRow key={el.id}>
                      <HiddenItemName title={el.name}>
                        {el.type === 'product' ? 'Produto' : 'Ingresso'} · {el.name}
                      </HiddenItemName>

                      <HiddenCodeChip title={el.hidden_code}>{el.hidden_code}</HiddenCodeChip>

                      <HiddenCopyButton
                        type='button'
                        onClick={() => copyLink(hiddenItemUrl(el), 'Link com o código copiado!')}
                      >
                        <Copy size={12} aria-hidden='true' />
                        Copiar link
                      </HiddenCopyButton>
                    </HiddenLinkRow>
                  ))}

                  <HiddenLinksHint>
                    Quem abrir este link vê o item sem precisar digitar o código.
                  </HiddenLinksHint>
                </HiddenLinks>
              )}
            </HeroBody>
          </HeroCard>

          {/* ── INFO CARD ── */}
          <SectionCard>
            <SectionLabel>Informações</SectionLabel>

            <InfoItem>
              <IconWrap><MapPin size={16} color={panelAccent(colors.secondaryPurple)} /></IconWrap>
              {!data?.address ? (
                <InfoText muted>Local a definir</InfoText>
              ) : (
                <LinkPage isWrapper to={data?.link_map}>
                  <InfoText link>
                    {data?.address?.street?.name && `${data?.address?.street?.name},`}
                    {data?.address?.street?.district && ` Bairro ${data?.address?.street?.district}`}
                    {data?.address?.number && `, nº ${data?.address?.number}`}
                    {data?.address?.complement && ` - ${data?.address?.complement}`}
                  </InfoText>
                </LinkPage>
              )}
            </InfoItem>

            <InfoDivider />

            <InfoItem alignTop={data?.dates?.length > 1}>
              <IconWrap><Calendar size={16} color={panelAccent(colors.secondaryPurple)} /></IconWrap>
              <DateContainer>
                {data?.dates?.length > 1 ? (
                  <>
                    <DateLine>
                      <DayLabel>Dia 1</DayLabel>
                      <InfoText>
                        {date?.getDay(data?.dates[0]?.date)} de {date?.getMounthName(data?.dates[0]?.date)}
                        &nbsp;· {date?.getTimeWithoutSeconds(data?.dates[0]?.start)}
                        {data?.dates[0]?.end ? ` até ${date.getTimeWithoutSeconds(data?.dates[0]?.end)}` : ''}
                      </InfoText>
                    </DateLine>

                    <DateLine>
                      <DayLabel>Dia 2</DayLabel>
                      <InfoText>
                        {date?.getDay(data?.dates[1]?.date)} de {date?.getMounthName(data?.dates[1]?.date)}
                        &nbsp;· {date?.getTimeWithoutSeconds(data?.dates[1]?.start)}
                        {data?.dates[1]?.end ? ` até ${date.getTimeWithoutSeconds(data?.dates[1]?.end)}` : ''}
                      </InfoText>
                    </DateLine>

                    {data?.dates?.length >= 3 && (
                      <CronogramRow>
                        <Button
                          category='outline'
                          size='medium'
                          marginBottom='0'
                          onClick={() => setFullCronogramModal(true)}
                        >
                          Ver cronograma completo
                        </Button>
                        <CronogramModal
                          data={data?.dates}
                          visible={fullCronogramModal}
                          setVisible={setFullCronogramModal}
                        />
                      </CronogramRow>
                    )}
                  </>
                ) : (
                  <InfoText>
                    {data?.dates?.[0] && date?.getDay(data?.dates[0]?.date)}
                    {data?.dates?.[0] && ` de ${date?.getMounthName(data?.dates[0]?.date)}`}
                    {data?.dates?.[0] && ` · ${date?.getTimeWithoutSeconds(data?.dates[0]?.start)}`}
                    {data?.dates?.[0]?.end ? ` até ${date.getTimeWithoutSeconds(data?.dates[0]?.end)}` : ''}
                  </InfoText>
                )}
              </DateContainer>
            </InfoItem>
          </SectionCard>

          {/* ── ACCESS CARD ── */}
          {!isDraft && (
            <SectionCard>
              <SectionLabel>Acessos</SectionLabel>
              {can(EVENT_PERMISSIONS.SALES_VIEW)
                ? <EventAccessCard eventId={data?.id} />
                : <MaskedStats labels={['Visualizações', 'Cliques em comprar', 'Vendas aprovadas', 'Taxa de conversão']} />}
            </SectionCard>
          )}

          {/* ── DESCRIPTION CARD ── */}
          {(data?.description_with_html || data?.description) && (
            <SectionCard>
              <SectionLabel>Descrição</SectionLabel>

              {data?.description_with_html ? (
                <>
                  <DescriptionContent
                    dangerouslySetInnerHTML={{
                      __html: showFullText
                        ? data.description_with_html
                        : truncateHTML(data.description_with_html, 300),
                    }}
                  />
                  {data.description_with_html.length > 300 && (
                    <ShowMoreButton onClick={handleShowMore}>
                      {showFullText ? (
                        <><ChevronUp size={14} /> Ver menos</>
                      ) : (
                        <><ChevronDown size={14} /> Ver descrição completa</>
                      )}
                    </ShowMoreButton>
                  )}
                </>
              ) : (
                <>
                  <DescriptionText>
                    {data?.description?.length > 300 && !showFullText
                      ? `${data.description.slice(0, 285)}...`
                      : data?.description}
                  </DescriptionText>
                  {data?.description?.length > 300 && (
                    <ShowMoreButton onClick={() => setShowFullText(!showFullText)}>
                      {showFullText ? (
                        <><ChevronUp size={14} /> Ver menos</>
                      ) : (
                        <><ChevronDown size={14} /> Ver mais</>
                      )}
                    </ShowMoreButton>
                  )}
                </>
              )}
            </SectionCard>
          )}

          {/* ── TICKETS ── */}
          {!!hasTickets && (
            <SectionCard noPad>
              <TicketsForSale data={data?.products} offlineStats={offlineStats} />
            </SectionCard>
          )}

          {/* ── PRODUCTS ── */}
          {!!hasProducts && (
            <SectionCard noPad>
              <ProductsForSale data={data?.products} offlineStats={offlineProductStats} />
            </SectionCard>
          )}
        </>
      ) : (
        <LoaderWrap>
          <Loader />
        </LoaderWrap>
      )}
    </ContainerDetail>
  )
}

/* ─────────────────────────────────────────
   Animations
───────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─────────────────────────────────────────
   Layout
───────────────────────────────────────── */
const ContainerDetail = styled(Column)`
  min-height: 100vh;
  padding: 0 0 32px;
  gap: 12px;
  animation: ${fadeUp} 0.35s ease both;
`;

const LoaderWrap = styled(FullyCentered)`
  height: 100vh;
`;

/* ─────────────────────────────────────────
   Hero Card
───────────────────────────────────────── */
const HeroCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 14px;
  background: ${colors.neutralLight};
  border: 1px solid rgba(0,0,0,0.07);
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);

  @media screen and (min-width: ${breakpoints.lg}) {
    flex-direction: row;
    align-items: stretch;
    gap: 24px;
    padding: 20px;
  }
`;

/* Proporção da capa pública, não o tamanho: a página real usa altura fixa
   (PageCover.js → StyledBackground) e largura em %, então o retângulo varia
   com a viewport — de ~2:1 (1440px) a ~2.7:1 (1920px). 35:19 (280x152) é o
   valor calibrado a olho pra esse card. Largura fixa + `aspect-ratio` no
   lugar de duas medidas independentes: width:320px ao lado de height:280px
   dava quase um quadrado (~1.14:1), não o retângulo largo da capa.

   `align-self: flex-start` trava a altura no valor da proporção — sem isso
   o `stretch` do row (necessário pro padding do HeroCard ficar igual nos
   4 lados mesmo com body mais alto) esticava a imagem além da proporção
   pretendida sempre que o conteúdo ao lado era mais alto que ela.

   O padding vem do HeroCard (mesma folga dos quatro lados), não daqui: a
   imagem tem sua própria borda arredondada e nunca fica colada nas bordas
   do card. */
const HeroImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 35 / 19;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 12px;

  & > div {
    width: 100% !important;
    height: 100% !important;
    border-radius: 0 !important;
  }

  & > div > img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover !important;
    border-radius: 0 !important;
  }

  @media screen and (min-width: ${breakpoints.lg}) {
    width: 280px;
    align-self: flex-start;
  }
`;

const HeroBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  min-width: 0;

  @media screen and (min-width: ${breakpoints.lg}) {
    flex: 1;
    padding: 20px 26px;
  }
`;

/* ─────────────────────────────────────────
   Badges
───────────────────────────────────────── */
const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const BaseBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: capitalize;
  white-space: nowrap;
`;

const StatusBadge = styled(BaseBadge).withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
})`
  background: ${({ active }) => active ? '#f0f0f0' : '#e8f5ee'};
  color: ${({ active }) => active ? colors.night : '#1a7f45'};
`;

const TypeBadge = styled(BaseBadge)`
  background: ${({ type }) =>
    type === eventTypes?.free?.slug ? eventTypes?.free?.color :
    type === eventTypes?.paid?.slug ? eventTypes?.paid?.color :
    colors.neutralDark};
  color: ${({ type }) => type === eventTypes?.paid?.slug ? colors.neutralLight : colors.nightDark};
`;

const AdultBadge = styled(BaseBadge)`
  background: ${colors.nightDark};
  color: ${colors.neutralLight};
  min-width: 34px;
`;

const TagBadge = styled(BaseBadge)`
  background: ${colors.goldLight}33;
  color: ${colors.goldDark};
  border: 1px solid ${colors.goldLight}66;
`;

/* ─────────────────────────────────────────
   Event Name + Link
───────────────────────────────────────── */
const EventName = styled.h1`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: ${colors.nightDark};
  line-height: 1.2;
  letter-spacing: -0.3px;

  @media screen and (min-width: ${breakpoints.lg}) {
    font-size: 23px;
  }
`;

/* LinkPage com isWrapper aplica width:100% no <a> que ele renderiza — dentro
   de uma linha flex isso faz o link invisível ocupar a linha inteira, e o
   botão de copiar (que vem depois dele) fica empurrado lá pro final, longe
   do texto. Aqui o link precisa encolher pro conteúdo, não preencher a linha. */
const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  min-width: 0;

  & > a {
    width: auto !important;
    min-width: 0;
    overflow: hidden;
  }
`;

const LinkText = styled.span`
  font-size: 12px;
  color: ${colors.primaryDark};
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
  cursor: pointer;
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  background: ${panelAccent(colors.secondaryPurple)};
  transition: background 0.2s ease, transform 0.15s ease;

  &:hover {
    background: ${panelAccent(colors.occasionalPurple)};
    transform: scale(1.08);
  }
`;

/* ─────────────────────────────────────────
   Links de itens ocultos
───────────────────────────────────────── */
const HiddenLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${panelAccentAlpha(colors.secondaryPurple, '33')};
  background: ${panelAccentAlpha(colors.secondaryPurple, '0D')};
`;

const HiddenLinksLabel = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: #6D14CC;
`;

const HiddenLinkRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const HiddenItemName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.nightDark};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const HiddenCodeChip = styled.code`
  display: inline-flex;
  align-items: center;
  max-width: 140px;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px dashed ${panelAccentAlpha(colors.secondaryPurple, '66')};
  background: #fff;
  color: ${colors.nightDark};
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HiddenCopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid rgba(16, 24, 40, 0.14);
  background: #fff;
  color: ${colors.nightDark};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: ${panelAccent(colors.secondaryPurple)};
    background: ${colors.neutralLight};
  }

  &:focus-visible {
    outline: 2px solid ${panelAccent(colors.secondaryPurple)};
    outline-offset: 2px;
  }
`;

const HiddenLinksHint = styled.p`
  margin: 0;
  font-size: 11px;
  line-height: 1.4;
  color: ${colors.night};
`;

/* ─────────────────────────────────────────
   Section Card (reusable container)
───────────────────────────────────────── */
const SectionCard = styled.div`
  background: ${colors.neutralLight};
  border-radius: 14px;
  border: 1px solid rgba(0,0,0,0.07);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  padding: ${({ noPad }) => noPad ? '16px 18px' : '18px'};
  animation: ${fadeUp} 0.35s ease both;
`;

const SectionLabel = styled.p`
  margin: 0 0 14px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${colors.nightLight};
`;

/* ─────────────────────────────────────────
   Info Rows (location / date)
───────────────────────────────────────── */
const InfoItem = styled.div`
  display: flex;
  align-items: ${({ alignTop }) => alignTop ? 'flex-start' : 'center'};
  gap: 10px;
  padding: 2px 0;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${panelAccentAlpha(colors.secondaryPurple, '14')};
`;

const InfoText = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ muted }) => muted ? colors.nightLight : colors.nightDark};
  text-decoration: ${({ link }) => link ? 'underline' : 'none'};
  text-underline-offset: 2px;
  cursor: ${({ link }) => link ? 'pointer' : 'default'};
`;

const InfoDivider = styled.div`
  height: 1px;
  background: rgba(0,0,0,0.06);
  margin: 12px 0;
`;

/* ─────────────────────────────────────────
   Dates
───────────────────────────────────────── */
const DateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const DateLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const DayLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${panelAccent(colors.secondaryPurple)};
  background: ${panelAccentAlpha(colors.secondaryPurple, '14')};
  padding: 2px 7px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CronogramRow = styled.div`
  margin-top: 8px;
`;

/* ─────────────────────────────────────────
   Description
───────────────────────────────────────── */
const DescriptionContent = styled.div`
  font-size: 13px;
  line-height: 1.7;
  color: ${colors.night};
  text-align: left;

  p, span, div { color: ${colors.night}; }
  strong, b    { color: ${colors.nightDark}; }

  img {
    max-width: 100%;
    height: auto;
  }
`;

const DescriptionText = styled.p`
  font-size: 13px;
  line-height: 1.7;
  color: ${colors.night};
  margin: 0;
`;

const ShowMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${panelAccentAlpha(colors.secondaryPurple, '55')};
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: ${panelAccent(colors.secondaryPurple)};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${panelAccent(colors.secondaryPurple)};
    color: ${colors.neutralLight};
    border-color: ${panelAccent(colors.secondaryPurple)};
  }
`;
